/**
 * Plan complexe, mode « Racines » : les n solutions de zⁿ = 1, sommets d'un
 * polygone régulier inscrit dans le cercle unité ; valeurs exactes quand
 * l'angle 2π/n est remarquable (n = 2, 3, 4, 6, 8, 12).
 */

import { useState } from 'react';
import { Note, Stepper } from '../../ui/controls';
import { frenchSpaces, superscript } from '../../ui/format';
import { makeFrame } from '../../ui/frame';
import { HaloText, INK } from '../../ui/svg-parts';
import { unityRoot } from './complex';
import { Box, Formula, Plane, RES_COLOR, SIZE, SOFT, approxZTex, px } from './parts';

const RF = makeFrame({ xMin: -1.6, xMax: 1.6, yMin: -1.6, yMax: 1.6 }, SIZE);
const ORDINAL: Record<number, string> = {
  2: 'carrées',
  3: 'cubiques',
  4: 'quatrièmes',
  5: 'cinquièmes',
  6: 'sixièmes',
  7: 'septièmes',
  8: 'huitièmes',
  9: 'neuvièmes',
  10: 'dixièmes',
  11: 'onzièmes',
  12: 'douzièmes',
};
const SUB = '₀₁₂₃₄₅₆₇₈₉';
const sub = (k: number) =>
  String(k)
    .split('')
    .map((c) => SUB[Number(c)])
    .join('');

export function RootsMode() {
  const [n, setN] = useState(6);
  const roots = Array.from({ length: n }, (_, k) => unityRoot(n, k));
  const O = px({ a: 0, b: 0 }, RF);
  const poly = roots
    .map((r) => `${RF.tx(r.point.a).toFixed(2)},${RF.ty(r.point.b).toFixed(2)}`)
    .join(' ');

  return (
    <div className="space-y-4">
      <div className="max-w-56">
        <Stepper label="n" value={n} min={2} max={12} onChange={setN} />
      </div>

      <Plane
        f={RF}
        step={0.5}
        label={`Les ${n} racines ${ORDINAL[n]} de l’unité, sur le cercle de centre O et de rayon 1`}
      >
        <circle
          cx={O.x}
          cy={O.y}
          r={RF.unit}
          fill="none"
          stroke={SOFT}
          strokeWidth={1.5}
          aria-hidden
        />
        {n > 2 ? (
          <polygon
            points={poly}
            fill={RES_COLOR}
            fillOpacity={0.1}
            stroke={RES_COLOR}
            strokeWidth={2}
            aria-hidden
          />
        ) : (
          <polyline points={poly} fill="none" stroke={RES_COLOR} strokeWidth={2} aria-hidden />
        )}
        {roots.map((r, k) => {
          const p = px(r.point, RF);
          const ang = (2 * Math.PI * k) / n;
          return (
            <g key={k} aria-hidden>
              <circle cx={p.x} cy={p.y} r={6} fill={RES_COLOR} />
              <HaloText
                x={O.x + (RF.unit + 22) * Math.cos(ang)}
                y={O.y - (RF.unit + 22) * Math.sin(ang)}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={14}
                fontWeight={700}
                fill={INK}
              >
                {`ω${sub(k)}`}
              </HaloText>
            </g>
          );
        })}
      </Plane>

      <div className="space-y-2">
        <Box title={`Les ${n} solutions de z${superscript(n)} = 1`} testId="cp-roots">
          <Formula
            tex={`z^{${n}} = 1 \\iff z = e^{i\\frac{2k\\pi}{${n}}},\\ k \\in \\{0, \\dots, ${n - 1}\\}`}
          />
          <ul className="space-y-0.5">
            {roots.map((r, k) => (
              <li key={k}>
                <Formula
                  tex={`\\omega_{${k}} = ${r.tex}${k === 0 ? '' : r.exact ? ` = ${r.exact}` : ` \\approx ${approxZTex(r.point)}`}`}
                />
              </li>
            ))}
          </ul>
        </Box>
        {n === 2 ? (
          <Note>
            {frenchSpaces(
              'Les deux racines carrées de 1 sont 1 et −1, symétriques par rapport à O.',
            )}
          </Note>
        ) : (
          <Box title="Un polygone régulier">
            <p className="text-ink-700 text-sm">
              {frenchSpaces(
                `Les ${n} racines sont les sommets d’un polygone régulier à ${n} côtés inscrit dans le cercle unité : on passe de l’une à la suivante en tournant de 2π/${n}.`,
              )}
            </p>
            <Formula
              tex={`\\omega_k = \\omega_1^{\\,k} \\qquad 1 + \\omega_1 + \\omega_1^2 ${n > 3 ? `+ \\dots + \\omega_1^{${n - 1}} ` : ''}= 0`}
            />
            <p className="text-ink-600 text-sm">
              {frenchSpaces('Leur somme est nulle : le polygone est centré en O.')}
            </p>
          </Box>
        )}
      </div>
    </div>
  );
}
